import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Img, { BaseImgProps } from './Img';
import VideoLabel from '@oberoninternal/travelbase-ds/components/primitive/VideoLabel';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { Box } from '@rebass/grid';
import Link, { LinkProps } from 'next/link';
import React, { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import Rating from './Rating';

import { getAssignmentColor } from '@oberoninternal/travelbase-ds/constants/theme';
import { useRouter } from 'next/router';
import { ActivityState } from '../utils/getActivityTileState';
import { useTenantContext } from '../context/TenantContext';

export interface ActivityTileProps {
    img?: BaseImgProps;
    rating?: number;
    title?: string;
    place?: string | null;
    themes?: string;
    duration?: string;
    time?: string;
    linkProps?: LinkProps;
    fullUrl?: string;

    icon?: ReactNode;
    videoUrl?: string | null;
    prices?: {
        name: string;
        price: number;
    }[];
    canBuyTickets?: boolean;
    large?: boolean;
    loading?: boolean;
    cta?: boolean;
    ticketSalesEnabled?: boolean;
    timeSlotChildren?: ReactNode;
    activityState?: ActivityState;
}

const ActivityTile: React.FC<React.PropsWithChildren<ActivityTileProps>> = ({
    img,
    rating,
    title,
    place,
    themes,
    duration,
    time,
    videoUrl,
    linkProps,
    large = false,
    loading = false,
    cta = false,
    ticketSalesEnabled = false,
    fullUrl,
    prices,
    canBuyTickets,
    timeSlotChildren,
    icon,
    activityState,
    ...rest
}) => {
    const freeAccess = prices?.length === 1 && prices[0].price === 0;
    const { brandConfig } = useTenantContext();
    const { locale } = useRouter();

    return (
        <Container
            itemScope
            itemType="http://schema.org/ListItem"
            className={large ? 'tile-xl' : 'tile-l'}
            canBuyTickets={activityState !== 'UNAVAILABLE'}
            {...rest}
        >
            {loading ? (
                <>
                    <Box
                        style={{
                            width: '100%',
                            paddingBottom: large ? 'calc(10 / 16 * 100%)' : 'calc(3 / 4 * 100%)',
                            position: 'relative',
                        }}
                    >
                        <Box style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }}>
                            <Skeleton width="100%" height="100%" />
                        </Box>
                    </Box>
                    <Information>
                        <Skeleton width="30%" height="100%" />
                        <Skeleton width="80%" height="100%" />
                        <Skeleton width="30%" height="100%" />
                    </Information>
                </>
            ) : (
                <>
                    <Photo>
                        {img && <Img loading="lazy" layout="ratio" ratio={large ? 10 / 16 : 3 / 4} {...img} />}
                        <VideoLabel videoUrl={videoUrl} />
                    </Photo>
                    <Information>
                        {!!rating && brandConfig.hasReviews && <Rating value={rating} />}
                        <div>
                            <Title variant="small">{title}</Title>
                            <Metadata>{[place, themes, duration, time].filter(i => !!i).join(', ')}</Metadata>
                        </div>
                        <StyledDateContainer>{timeSlotChildren}</StyledDateContainer>

                        {cta && (
                            <PurchaseContainer>
                                <>
                                    <Button as="a" style={{ zIndex: 1 }}>
                                        {ticketSalesEnabled ? (
                                            <FormattedMessage defaultMessage="Tijden en tickets" />
                                        ) : (
                                            <FormattedMessage defaultMessage="Meer info" />
                                        )}
                                    </Button>
                                </>
                            </PurchaseContainer>
                        )}

                        {prices && (
                            <PurchaseContainer>
                                <Prices>
                                    {freeAccess && (
                                        <Price>
                                            <Body>
                                                <FormattedMessage defaultMessage="gratis toegang" />
                                            </Body>
                                        </Price>
                                    )}
                                    {!freeAccess &&
                                        prices.map(price => (
                                            <Price key={price.name}>
                                                <Body>
                                                    {!!price.price && (
                                                        <FormattedNumber format="EUR" value={price.price} />
                                                    )}
                                                    {!price.price && <FormattedMessage defaultMessage="gratis" />}
                                                </Body>{' '}
                                                <Body>
                                                    {!price.price ? (
                                                        <FormattedMessage
                                                            defaultMessage="voor {name}"
                                                            values={{ name: price.name.toLowerCase() }}
                                                        />
                                                    ) : (
                                                        <FormattedMessage
                                                            defaultMessage="per {name}"
                                                            values={{
                                                                name:
                                                                    locale !== 'de'
                                                                        ? price.name.toLowerCase()
                                                                        : price.name,
                                                            }}
                                                        />
                                                    )}
                                                </Body>
                                            </Price>
                                        ))}
                                </Prices>

                                {activityState === 'AVAILABLE' && linkProps && (
                                    <Link {...linkProps} passHref legacyBehavior>
                                        <Button as="a" style={{ zIndex: 1 }}>
                                            <FormattedMessage defaultMessage="Ticket kopen" />
                                        </Button>
                                    </Link>
                                )}

                                {activityState === 'EXTERAL' && (
                                    <Body variant="large" style={{ fontWeight: 500 }}>
                                        <FormattedMessage defaultMessage="Meer info" />
                                    </Body>
                                )}
                                {activityState === 'UNAVAILABLE' && (
                                    <Body variant="large" style={{ fontWeight: 500 }}>
                                        <FormattedMessage defaultMessage="Niet beschikbaar" />
                                    </Body>
                                )}
                                {activityState === 'SOLD_OUT' && (
                                    <Body variant="large" style={{ fontWeight: 500 }}>
                                        <FormattedMessage defaultMessage="Uitverkocht" />
                                    </Body>
                                )}
                            </PurchaseContainer>
                        )}
                        {linkProps && (
                            <Link {...linkProps} passHref legacyBehavior>
                                <LinkOverlay />
                            </Link>
                        )}
                    </Information>
                    {icon && <IconWrapper>{icon}</IconWrapper>}
                    <meta itemProp="name" content={`${title} - ${place} - ${time ?? ''}`} />
                    {fullUrl && <meta itemProp="url" content={fullUrl} />}
                </>
            )}
        </Container>
    );
};

const Container = styled.div<Pick<ActivityTileProps, 'canBuyTickets'>>`
    display: block;
    position: relative;
    opacity: ${({ canBuyTickets }) => (canBuyTickets ? 1 : 0.5)};

    & > img {
        width: 100%;
    }

    & > * + * {
        margin-top: 0.8rem;
    }
`;

const Photo = styled.div`
    position: relative;
    margin-bottom: ${({ theme }) => theme.spacing['30_Small']};
`;

const Information = styled.div`
    & > * + * {
        margin-top: 0.4rem;
    }
`;

const Metadata = styled(Body)``;

const PurchaseContainer = styled.div`
    margin-top: 1.2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 0.8rem;
    > a {
        flex-shrink: 0;
        margin-bottom: auto;
    }
`;

const Prices = styled.div``;

const Price = styled.div`
    & > * {
        display: inline;
        line-height: 1.5em;

        &:first-child {
            font-weight: 700;
        }
    }
`;

const LinkOverlay = styled.a`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
`;
const StyledDateContainer = styled.div`
    font-weight: 700;
    color: ${({ theme }) =>
        theme.colorAssignments.rating
            ? getAssignmentColor(theme, theme.colorAssignments.rating)
            : theme.colors.secondary['30']};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const IconWrapper = styled.div`
    z-index: 1;
    position: absolute;
    right: 1.2rem;
    top: 1.2rem;
`;

export default ActivityTile;
